import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { Button } from '../components/ui/button';
import { Calendar, Phone, MapPin, ChevronDown, ChevronUp, Download, Package, User, Star, Trash2 } from 'lucide-react';
import { format, parseISO } from 'date-fns';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EXPERIENCE_COLORS = {
  'Interested': 'bg-emerald-100 text-emerald-700',
  'Not Interested': 'bg-red-100 text-red-700',
  'Neutral': 'bg-amber-100 text-amber-700',
  'Positive': 'bg-emerald-100 text-emerald-700',
  'Negative': 'bg-red-100 text-red-700',
};

const CallHistory = ({ onLogout }) => {
  const [calls, setCalls] = useState([]);
  const [loading, setLoading] = useState(true);
  const [expandedCall, setExpandedCall] = useState(null);
  const [filterType, setFilterType] = useState('all');
  const navigate = useNavigate();

  useEffect(() => { fetchCalls(); }, []);

  const fetchCalls = async () => {
    try {
      const token = localStorage.getItem('token');
      const r = await axios.get(`${API}/sales-calls`, { headers: { Authorization: `Bearer ${token}` } });
      setCalls(r.data);
    } catch { toast.error('Failed to load call history'); }
    finally { setLoading(false); }
  };

  const handleExportCalls = () => {
    if (!calls.length) { toast.error('No calls to export'); return; }
    const rows = [['Call Date','Account','Call Type','Who Met','Contact','Category','Product','Experience','Follow-up Actions','Workshop/Payment Outcome','Personal Notes','Follow-up Date']];
    calls.forEach(call => {
      const isV2 = call.call_version === 'v2';
      if (isV2) {
        if (call.products?.length > 0) {
          call.products.forEach((p, i) => {
            rows.push([
              call.call_date||'', i===0?call.account_name:'', i===0?call.call_type:'',
              i===0?call.who_met:'', i===0?call.contact_person_name:'', i===0?call.call_category:'',
              p.product_name||'', p.call_experience||'',
              [...(p.follow_up_actions||[]), p.follow_up_others_text].filter(Boolean).join(' | '),
              '', i===0?call.personal_notes:'', i===0?(call.follow_up_date||''):''
            ]);
          });
        } else {
          rows.push([call.call_date||'', call.account_name||'', call.call_type||'', call.who_met||'',
            call.contact_person_name||'', call.call_category||'', '',
            call.workshop_outcome||call.payment_outcome||'', '',
            call.workshop_outcome||call.payment_outcome||'', call.personal_notes||'', call.follow_up_date||'']);
        }
      } else {
        rows.push([call.call_date||'', call.account_name||'', '', call.visit_meeting_status||'',
          call.contact_person_name||'', '', '', '', '', '', '', call.next_visit_date||'']);
      }
    });
    const csv = rows.map(r => r.map(v => `"${String(v).replace(/"/g,'""')}"`).join(',')).join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `VoiceCRM_Calls_${new Date().toISOString().slice(0,10)}.csv`; a.click();
    URL.revokeObjectURL(url);
    toast.success('Exported successfully!');
  };

  const filteredCalls = calls.filter(c => {
    if (filterType === 'all') return true;
    if (filterType === 'v2') return c.call_version === 'v2';
    if (filterType === 'legacy') return c.call_version !== 'v2';
    return true;
  });

  const getCategoryColor = cat => {
    const map = {
      'Product Promotion Call': 'bg-teal-100 text-teal-700',
      'Complaint Addressal Call': 'bg-red-100 text-red-700',
      'Workshop Invite Call': 'bg-cyan-100 text-cyan-700',
      'Payment Follow-Up Call': 'bg-emerald-100 text-emerald-700',
      'Others': 'bg-slate-100 text-slate-600',
    };
    return map[cat] || 'bg-slate-100 text-slate-600';
  };

  return (
    <Layout onLogout={onLogout}>
      <div className="p-8">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-4xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>Call History</h1>
            <p className="text-base text-slate-600 mt-2">{calls.length} total calls recorded</p>
          </div>
          <div className="flex items-center gap-3">
            <Button onClick={handleExportCalls} variant="outline"
              className="border-emerald-300 text-emerald-700 hover:bg-emerald-50 rounded-xl px-5 py-3 font-semibold">
              <Download className="mr-2 h-4 w-4" /> Export CSV
            </Button>
            <Button onClick={() => navigate('/calls/new')}
              className="bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl px-6 py-3 font-semibold">
              <Phone className="mr-2 h-4 w-4" /> New Call
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-2 mb-6">
          {[['all','All Calls'],['v2','New Format'],['legacy','Legacy']].map(([key,label]) => (
            <button key={key} onClick={() => setFilterType(key)}
              className={`px-4 py-2 rounded-xl text-sm font-semibold border-2 transition-all ${
                filterType === key ? 'bg-teal-700 text-white border-violet-600' : 'bg-white text-slate-600 border-slate-200 hover:border-teal-300'}`}>
              {label}
            </button>
          ))}
        </div>

        <div className="bg-white/80 backdrop-blur-xl rounded-2xl border border-slate-200/50 shadow-xl">
          <div className="p-6">
            {loading ? (
              <div className="text-center py-12">
                <div className="inline-block animate-spin rounded-full h-8 w-8 border-4 border-teal-500 border-t-transparent" />
                <p className="text-slate-500 mt-4">Loading...</p>
              </div>
            ) : filteredCalls.length === 0 ? (
              <div className="text-center py-16">
                <Phone className="h-10 w-10 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500">No calls found</p>
                <Button onClick={() => navigate('/calls/new')} className="mt-4 bg-gradient-to-r from-teal-700 to-emerald-500 text-white rounded-xl">
                  Log First Call
                </Button>
              </div>
            ) : (
                            <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b-2 border-slate-100">
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Type</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Clinic</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Contact</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Category / Purpose</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Follow-Up Action</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Follow-Up Date</th>
                      <th className="text-left py-3 px-4 text-xs font-semibold text-slate-500 uppercase tracking-wide">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-50">
                    {filteredCalls.map(call => {
                      const isExpanded = expandedCall === call.id;
                      const isV2 = call.call_version === 'v2';
                      let dateStr = '';
                      try { dateStr = format(parseISO(call.call_date), 'dd MMM yy'); } catch { dateStr = call.call_date || ''; }
                      const followUpDate = call.follow_up_date || call.next_visit_date || '';
                      let followUpStr = '';
                      try { followUpStr = followUpDate ? format(parseISO(followUpDate), 'dd MMM yy') : '—'; } catch { followUpStr = followUpDate || '—'; }
                      const category = call.call_category || (call.telephonic_purposes?.join(', ')) || call.visit_meeting_status || '—';
                      const followUpActions = call.follow_up_actions?.join(', ') || '—';
                      const callType = call.call_type || (isV2 ? 'Field Call' : 'Legacy');

                      return (
                        <>
                          <tr key={call.id}
                            className={`hover:bg-teal-50/30 cursor-pointer transition-colors ${isExpanded ? 'bg-teal-50/50' : ''}`}
                            onClick={() => setExpandedCall(isExpanded ? null : call.id)}>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span className="text-xs font-medium text-slate-700">{dateStr}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`text-xs px-2 py-1 rounded-lg font-medium ${
                                callType.includes('Telephonic') ? 'bg-blue-100 text-blue-700' : 'bg-teal-100 text-teal-700'
                              }`}>
                                {callType.includes('Telephonic') ? '📞 Telephonic' : '🤝 Field'}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="font-semibold text-slate-800 text-sm">{call.account_name}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className="text-slate-600 text-xs">{call.contact_person_name || '—'}</span>
                            </td>
                            <td className="py-3 px-4">
                              <span className={`text-xs px-2 py-0.5 rounded-lg font-medium ${getCategoryColor(call.call_category)}`}>
                                {category.length > 30 ? category.slice(0, 30) + '...' : category}
                              </span>
                            </td>
                            <td className="py-3 px-4 max-w-[180px]">
                              <span className="text-xs text-slate-500 line-clamp-2">{followUpActions}</span>
                            </td>
                            <td className="py-3 px-4 whitespace-nowrap">
                              <span className={`text-xs font-semibold ${followUpStr !== '—' ? 'text-emerald-600' : 'text-slate-300'}`}>
                                {followUpStr}
                              </span>
                            </td>
                            <td className="py-3 px-4">
                              <div className="flex items-center gap-2">
                              {localStorage.getItem('isAdmin') === 'true' && (
                                <button type="button" onClick={e => {
                                  e.stopPropagation();
                                  if (!window.confirm('Delete this call? This cannot be undone.')) return;
                                  const token = localStorage.getItem('token');
                                  fetch(`${API}/sales-calls/${call.id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } })
                                    .then(r => { if (r.ok) { toast.success('Call deleted ✅'); fetchCalls(); } else toast.error('Failed to delete'); })
                                    .catch(() => toast.error('Failed to delete'));
                                }}
                                  className="p-1.5 rounded-lg hover:bg-red-50 text-slate-300 hover:text-red-500 transition-colors">
                                  <Trash2 className="h-3.5 w-3.5" />
                                </button>
                              )}
                              {isExpanded ? <ChevronUp className="h-4 w-4 text-slate-400" /> : <ChevronDown className="h-4 w-4 text-slate-400" />}
                            </div>
                            </td>
                          </tr>
                          {isExpanded && (
                            <tr key={`${call.id}-details`}>
                              <td colSpan="8" className="px-4 pb-4 pt-2 bg-slate-50/50">
                                <div className="space-y-3 pl-2 border-l-2 border-teal-200">
                                  {isV2 ? (
                                    <>
                                      {call.telephonic_purposes_others && (
                                        <div className="p-3 bg-teal-50 rounded-xl border border-teal-200">
                                          <p className="text-xs font-semibold text-teal-700 mb-1">📞 Purpose Notes</p>
                                          <p className="text-sm text-slate-700">{call.telephonic_purposes_others}</p>
                                        </div>
                                      )}
                                      {call.products_discussed?.length > 0 && (
                                        <div className="p-3 bg-violet-50 rounded-xl border border-violet-200 space-y-2">
                                          <p className="text-xs font-semibold text-violet-700 uppercase tracking-wide">💊 Products Discussed</p>
                                          <div className="flex flex-wrap gap-1">
                                            {call.products_discussed.map((p, i) => <span key={i} className="text-xs px-2 py-1 bg-violet-100 text-violet-800 rounded-lg">{p}</span>)}
                                          </div>
                                          {call.products_higher_interest?.length > 0 && (
                                            <div className="flex flex-wrap gap-1 mt-1">
                                              <span className="text-xs text-slate-400 mr-1">Higher Interest:</span>
                                              {call.products_higher_interest.map((p, i) => <span key={i} className="text-xs px-2 py-0.5 bg-emerald-100 text-emerald-700 rounded-lg font-medium">⭐ {p}</span>)}
                                            </div>
                                          )}
                                        </div>
                                      )}
                                      {call.complaint_products?.length > 0 && (
                                        <div className="p-3 bg-red-50 rounded-xl border border-red-200 space-y-2">
                                          <p className="text-xs font-semibold text-red-700 uppercase tracking-wide">⚠️ Complaint</p>
                                          <div className="flex flex-wrap gap-1">
                                            {call.complaint_products.map((p, i) => <span key={i} className="text-xs px-2 py-1 bg-red-100 text-red-800 rounded-lg">{p}</span>)}
                                          </div>
                                          {call.complaint_closed && <p className="text-xs text-slate-600">Closed: <strong className={call.complaint_closed === 'Yes' ? 'text-emerald-600' : 'text-red-600'}>{call.complaint_closed}</strong></p>}
                                        </div>
                                      )}
                                      {(call.workshop_name || call.workshop_date) && (
                                        <div className="p-3 bg-cyan-50 rounded-xl border border-cyan-200 space-y-1">
                                          <p className="text-xs font-semibold text-cyan-700 uppercase tracking-wide">🎓 Workshop / Webinar</p>
                                          {call.workshop_name && <p className="text-sm font-medium text-slate-800">{call.workshop_name}</p>}
                                          {call.workshop_date && <p className="text-xs text-slate-500">Date: {call.workshop_date}</p>}
                                          {call.workshop_response && <p className="text-xs text-slate-600">Response: <strong>{call.workshop_response}</strong></p>}
                                        </div>
                                      )}
                                      {/* Follow-Up Actions */}
                                      {call.follow_up_actions?.length > 0 && (
                                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200 space-y-2">
                                          <p className="text-xs font-semibold text-amber-700 uppercase tracking-wide">✅ Follow-Up Actions</p>
                                          <div className="flex flex-wrap gap-1">
                                            {call.follow_up_actions.map((a, i) => (
                                              <span key={i} className="text-xs px-2 py-1 bg-amber-100 text-amber-800 rounded-lg">{a}</span>
                                            ))}
                                          </div>
                                        </div>
                                      )}
                                      {/* Call Rating */}
                                      {call.call_experience !== undefined && call.call_experience !== '' && call.call_experience !== 0 && (
                                        <div className="flex items-center gap-2">
                                          <p className="text-xs text-slate-400">Call Rating:</p>
                                          <div className="flex gap-0.5">
                                            {[1,2,3,4,5].map(n => (
                                              <span key={n} className={`text-sm ${Number(call.call_experience) >= n ? 'text-amber-400' : 'text-slate-200'}`}>★</span>
                                            ))}
                                          </div>
                                          <span className="text-xs text-slate-500">{call.call_experience}/5</span>
                                        </div>
                                      )}
                                                                            {call.personal_notes && (
                                        <div className="p-3 bg-amber-50 rounded-xl border border-amber-200">
                                          <p className="text-xs text-amber-600 font-semibold mb-1">📝 Personal Notes</p>
                                          <p className="text-sm text-slate-700">{call.personal_notes}</p>
                                        </div>
                                      )}
                                      {call.call_experience !== undefined && call.call_experience !== '' && call.call_experience !== 0 && (
                                        <div className="flex items-center gap-2">
                                          <p className="text-xs text-slate-400">Call Rating:</p>
                                          <div className="flex gap-0.5">
                                            {[1,2,3,4,5].map(n => <span key={n} className={`text-sm ${Number(call.call_experience) >= n ? 'text-amber-400' : 'text-slate-200'}`}>★</span>)}
                                          </div>
                                          <span className="text-xs text-slate-500">{call.call_experience}/5</span>
                                        </div>
                                      )}
                                    </>
                                  ) : (
                                    <div className="space-y-2">
                                      {call.next_visit_planned_in && <p className="text-sm text-slate-500">Next visit in: <strong>{call.next_visit_planned_in}</strong></p>}
                                    </div>
                                  )}
                                  <Button variant="ghost" size="sm"
                                    className="text-teal-700 hover:text-teal-700 hover:bg-teal-50 mt-1"
                                    onClick={() => navigate(`/accounts/${call.account_id}`)}>
                                    <MapPin className="h-4 w-4 mr-1" /> View Account
                                  </Button>
                                </div>
                              </td>
                            </tr>
                          )}
                        </>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default CallHistory;
